// src/theme/design-constants.tsx
import React from 'react';

export type DesignConstant = {
  id: string;
  name: string;
  category: 'character' | 'decoration' | 'effect';
  component: React.ComponentType<DesignConstantProps>;
  thumbnail?: string;
  price?: number; // for marketplace
  isPremium?: boolean;
};

export type DesignConstantProps = {
  size?: number;
  color?: string;
  colors?: string[];
  className?: string;
  animationSpeed?: number;
};

// ============ CHARACTERS ============

export function PixelatedTurtle({
  size = 60,
  color = '#10b981',
  className = ""
}: DesignConstantProps) {
  return (
    <img
      src="/api/assets/turtle-floating"
      alt="Turtle"
      width={size}
      height={size}
      className={className}
      style={{
        filter: color !== '#10b981' ? `hue-rotate(${getHueRotation(color)}deg) saturate(1.2)` : 'none',
        imageRendering: 'auto'
      }}
    />
  );
}

// Helper function to calculate hue rotation based on color
function getHueRotation(targetColor: string): number {
  // Base green color is #10b981
  // This is a simple approximation - for exact color matching you'd need more sophisticated color math
  const colorMap: Record<string, number> = {
    '#10b981': 0,    // green (base)
    '#14B8A6': 10,   // teal
    '#0ea5e9': 180,  // blue
    '#8b5cf6': 260,  // purple
    '#ec4899': 300,  // pink
    '#f97316': 30,   // orange
    '#fbbf24': 50,   // yellow
    '#94a3b8': 200,  // gray-blue
  };

  return colorMap[targetColor] || 0;
}

export const PixelatedEagle: React.FC<DesignConstantProps> = ({
  size = 60,
  color = '#94a3b8',
  className = ""
}) => (
  <img
    src="/api/assets/eagle-silhouette"
    alt="Eagle"
    width={size}
    height={size}
    className={className}
    style={{
      filter: color !== '#94a3b8' ? `hue-rotate(${getHueRotation(color)}deg) saturate(1.2)` : 'none',
      imageRendering: 'auto'
    }}
  />
);

export const PixelatedFirebird: React.FC<DesignConstantProps> = ({
  size = 60,
  color = '#f59e0b',
  className = ""
}) => (
  <img
    src="/api/assets/phoenix"
    alt="Phoenix"
    width={size}
    height={size}
    className={className}
    style={{
      filter: color !== '#f59e0b' ? `hue-rotate(${getHueRotation(color)}deg) saturate(1.2)` : 'none',
      imageRendering: 'auto'
    }}
  />
);

export const PixelatedBolt: React.FC<DesignConstantProps> = ({
  size = 60,
  color = '#ffd700',
  className = ""
}) => (
  <svg width={size} height={size} viewBox="0 0 32 32" className={className}>
    <g fill={color}>
      <rect x="16" y="4" width="4" height="4" />
      <rect x="14" y="8" width="4" height="4" />
      <rect x="12" y="12" width="4" height="4" />
      <rect x="10" y="16" width="8" height="2" />
      <rect x="14" y="18" width="4" height="4" />
      <rect x="12" y="22" width="4" height="6" />
    </g>
  </svg>
);

export const PixelatedCloud: React.FC<DesignConstantProps> = ({
  size = 60,
  color = '#38bdf8',
  className = ""
}) => (
  <img
    src="/api/assets/cloud"
    alt="Cloud"
    width={size}
    height={size}
    className={className}
    style={{
      filter: color !== '#38bdf8' ? `hue-rotate(${getHueRotation(color)}deg) saturate(1.2)` : 'none',
      imageRendering: 'auto'
    }}
  />
);

// ============ EFFECTS ============

export const PixelatedBubble: React.FC<DesignConstantProps> = ({
  size = 16,
  color = '#67e8f9',
  className = ""
}) => (
  <svg width={size} height={size} viewBox="0 0 16 16" className={className}>
    <g fill={color}>
      {/* Outer circle */}
      <rect x="4" y="2" width="8" height="2" opacity="0.9" />
      <rect x="2" y="4" width="2" height="8" opacity="0.9" />
      <rect x="12" y="4" width="2" height="8" opacity="0.9" />
      <rect x="4" y="12" width="8" height="2" opacity="0.9" />
      {/* Inner fill */}
      <rect x="4" y="4" width="8" height="8" opacity="0.7" />
      {/* Highlight/shine */}
      <rect x="6" y="4" width="2" height="2" fill="white" opacity="0.8" />
    </g>
  </svg>
);

// ============ DECORATIONS ============

export const PixelatedCoral: React.FC<DesignConstantProps> = ({
  colors = ['#f97316', '#ec4899', '#8b5cf6', '#10b981', '#0ea5e9'],
  size = 60,
  className = ""
}) => (
  <svg width={size} height={size} viewBox="0 0 32 32" className={className}>
    {/* Main coral trunk */}
    <rect x="14" y="24" width="4" height="6" fill={colors[0]} />
    {/* Left branch */}
    <rect x="10" y="20" width="4" height="4" fill={colors[1]} />
    <rect x="8" y="16" width="2" height="4" fill={colors[2]} />
    <rect x="6" y="14" width="2" height="2" fill={colors[3]} />
    {/* Center branch */}
    <rect x="14" y="18" width="4" height="6" fill={colors[0]} />
    <rect x="14" y="14" width="4" height="4" fill={colors[1]} />
    <rect x="12" y="12" width="2" height="2" fill={colors[4]} />
    <rect x="18" y="12" width="2" height="2" fill={colors[4]} />
    {/* Right branch */}
    <rect x="18" y="20" width="4" height="4" fill={colors[1]} />
    <rect x="22" y="16" width="2" height="4" fill={colors[2]} />
    <rect x="24" y="14" width="2" height="2" fill={colors[3]} />
    {/* Small polyps */}
    <rect x="12" y="10" width="2" height="2" fill={colors[3]} />
    <rect x="18" y="10" width="2" height="2" fill={colors[3]} />
    <rect x="16" y="16" width="2" height="2" fill={colors[4]} />
  </svg>
);

export const PixelatedSeaweed: React.FC<DesignConstantProps> = ({ 
  color = '#10b981',
  size = 40,
  className = ""
}) => (
  <svg width={size} height={size * 2} viewBox="0 0 20 40" className={className}>
    <g fill={color}>
      <rect x="8" y="0" width="4" height="8" />
      <rect x="6" y="8" width="4" height="8" />
      <rect x="8" y="16" width="4" height="8" />
      <rect x="10" y="24" width="4" height="8" />
      <rect x="8" y="32" width="4" height="8" />
    </g>
  </svg>
);

export const PixelatedMountain: React.FC<DesignConstantProps> = ({
  color = '#64748b',
  size = 80,
  className = ""
}) => (
  <img
    src="/api/assets/mountains"
    alt="Mountains"
    width={size}
    height={size}
    className={className}
    style={{
      filter: color !== '#64748b' ? `hue-rotate(${getHueRotation(color)}deg) saturate(1.2)` : 'none',
      imageRendering: 'auto'
    }}
  />
);

export const PixelatedSun: React.FC<DesignConstantProps> = ({ 
  color = '#fbbf24',
  size = 60,
  className = ""
}) => (
  <svg width={size} height={size} viewBox="0 0 32 32" className={className}>
    <g fill={color}>
      <rect x="12" y="12" width="8" height="8" />
      <rect x="14" y="4" width="4" height="4" />
      <rect x="14" y="24" width="4" height="4" />
      <rect x="4" y="14" width="4" height="4" />
      <rect x="24" y="14" width="4" height="4" />
      <rect x="6" y="6" width="3" height="3" />
      <rect x="23" y="6" width="3" height="3" />
      <rect x="6" y="23" width="3" height="3" />
      <rect x="23" y="23" width="3" height="3" />
    </g>
  </svg>
);

// ============ REGISTRY ============

export const DESIGN_CONSTANTS: Record<string, DesignConstant> = {
  'turtle': {
    id: 'turtle',
    name: 'Pixelated Turtle',
    category: 'character',
    component: PixelatedTurtle,
    isPremium: false,
  },
  'eagle': {
    id: 'eagle',
    name: 'Pixelated Eagle',
    category: 'character',
    component: PixelatedEagle,
    isPremium: false,
  },
  'firebird': {
    id: 'firebird',
    name: 'Fire Bird',
    category: 'character',
    component: PixelatedFirebird,
    isPremium: false,
  },
  'bolt': {
    id: 'bolt',
    name: 'Lightning Bolt',
    category: 'character',
    component: PixelatedBolt,
    isPremium: false,
  },
  'cloud': {
    id: 'cloud',
    name: 'Cloud',
    category: 'character',
    component: PixelatedCloud,
    isPremium: false,
  },
  'coral': {
    id: 'coral',
    name: 'Pixelated Coral',
    category: 'decoration',
    component: PixelatedCoral,
    isPremium: false,
  },
  'seaweed': {
    id: 'seaweed',
    name: 'Seaweed',
    category: 'decoration',
    component: PixelatedSeaweed,
    isPremium: false,
  },
  'mountain': {
    id: 'mountain',
    name: 'Mountain',
    category: 'decoration',
    component: PixelatedMountain,
    isPremium: false,
  },
  'sun': {
    id: 'sun',
    name: 'Sun',
    category: 'decoration',
    component: PixelatedSun,
    isPremium: false,
  },
  'bubbles': {
    id: 'bubbles',
    name: 'Bubbles',
    category: 'effect',
    component: PixelatedBubble,
    isPremium: false,
  },
};

export const getDesignConstant = (id: string): DesignConstant | undefined => {
  return DESIGN_CONSTANTS[id];
};

export const getDesignsByCategory = (category: DesignConstant['category']): DesignConstant[] => {
  return Object.values(DESIGN_CONSTANTS).filter(d => d.category === category);
};
